// FRE-293: client wrapper around POST /api/v1/onboarding/mint-token (FRE-291).
//
// Pure helpers (form validation + scope contract) live alongside the fetch
// wrapper so the drawer renderer stays thin. The validator mirrors the
// server-side `parseMintTokenBody` (`apps/worker/src/routes/onboarding.ts`) —
// kept in sync by hand because we don't share types across worker/dashboard.

import { apiPost } from "@/lib/api.ts";

// Hard-coded to the two scopes the worker accepts. Anything else (`files:delete`,
// cross-org) requires the full OAuth consent flow.
export const INLINE_MINTABLE_SCOPES = ["files:read", "files:write"] as const;
export type InlineMintableScope = (typeof INLINE_MINTABLE_SCOPES)[number];

export type MintTokenRequest = {
  organizationId: string;
  scopes: InlineMintableScope[];
  name?: string;
};

export type MintTokenResponse = {
  token: string;
  scopes: InlineMintableScope[];
  organizationId: string;
  expiresAt: string | null;
};

export async function mintToken(input: MintTokenRequest): Promise<MintTokenResponse> {
  return apiPost<MintTokenResponse>("/api/v1/onboarding/mint-token", input);
}

// Pure form-state validator. Returns `ok: true` and the request body when the
// drawer's pickers are in a submittable state, otherwise a stable error key.
//
// Kept separate from the React layer so it can be unit-tested without
// rendering the section. Error keys mirror the server-side ones for parity in
// error reporting where applicable; the dashboard never shows raw keys —
// the section renderer maps them to copy.
export type MintFormState = {
  organizationId: string | null;
  scopes: ReadonlySet<string>;
};

export type MintFormValidation =
  | { ok: true; request: MintTokenRequest }
  | { ok: false; error: "organization_required" | "scopes_required" | "scopes_invalid" };

export function validateMintForm(state: MintFormState): MintFormValidation {
  if (!state.organizationId || state.organizationId.length === 0) {
    return { ok: false, error: "organization_required" };
  }
  if (state.scopes.size === 0) {
    return { ok: false, error: "scopes_required" };
  }
  const allowed = new Set<string>(INLINE_MINTABLE_SCOPES);
  const scopes: InlineMintableScope[] = [];
  // Preserve INLINE_MINTABLE_SCOPES's order so submissions are deterministic.
  for (const s of INLINE_MINTABLE_SCOPES) {
    if (state.scopes.has(s)) scopes.push(s);
  }
  for (const s of state.scopes) {
    if (!allowed.has(s)) return { ok: false, error: "scopes_invalid" };
  }
  return {
    ok: true,
    request: { organizationId: state.organizationId, scopes },
  };
}
